import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { AccountContext, type AccountState } from "../account.js";
import { forgetChats } from "../chat-cache.js";
import { hostedApi, type HostedSession } from "../hosted-api.js";
import { LOCALES, setLocale, text, useLocale, useText, type Locale } from "../i18n.js";
import { LOGIN } from "../text/login.js";
import { serverText } from "../text/server.js";

import { BrandMark } from "./BrandMark.js";

/**
 * The app's language, on the screens shown before Settings can be reached
 * (signing in, the connect screen). Each name is in its own language, as in
 * Settings, so someone who cannot read the one on screen still finds theirs.
 */
export function LanguagePicker(): React.JSX.Element {
  const t = useText(LOGIN);
  const current = useLocale();
  return (
    <select className="language-pick" value={current} onChange={(event) => setLocale(event.target.value as Locale)} aria-label={t.language}>
      {LOCALES.map((one) => (
        <option key={one.id} value={one.id} lang={one.id}>
          {one.name}
        </option>
      ))}
    </select>
  );
}

export function Login({ onAuthenticated, tiers, signupUrl, demo = true }: { readonly onAuthenticated: (session: HostedSession) => void; readonly tiers?: HostedSession["tiers"]; readonly signupUrl?: string | undefined; readonly demo?: boolean | undefined }): React.JSX.Element {
  const t = useText(LOGIN);
  const [creating, setCreating] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [profile, setProfile] = useState<"lite" | "full">("full");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await (creating ? hostedApi.signup(username, password, profile) : hostedApi.login(username, password));
      setPassword("");
      onAuthenticated(session);
    } catch (cause) { setError(cause instanceof Error ? cause.message : t.couldNotSignIn); }
    finally { setBusy(false); }
  };

  return (
    <main className="login-page with-hero">
      <section className="login-hero" aria-hidden="true">
        <div className="login-hero-brand">
          <BrandMark />
          ark-bot
        </div>
        <div className="login-hero-words">
          <h2>
            {t.heroFirstLine}
            <br />
            {t.heroBefore}<em>{t.heroEmphasis}</em>{t.heroAfter}
          </h2>
          <p>{t.heroAbout}</p>
        </div>
        <span />
      </section>
      <div className="login-side">
        <form className="login-card" onSubmit={(event) => void submit(event)}>
          <div className="brand">{demo ? t.brandDemo : "ark-bot"}</div>
          <h1>{creating ? t.ownWorkspace : t.welcomeBack}</h1>
          <p className="login-description">{creating ? t.createAbout : t.signInAbout}</p>
          <label>{t.email}<input required autoFocus type="email" maxLength={254} placeholder={t.emailExample} value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" autoCapitalize="none" spellCheck={false} /></label>
          <label>{t.password}<input required minLength={8} maxLength={128} type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={creating ? "new-password" : "current-password"} /></label>
          {creating ? (
            <fieldset className="workspace-choices">
              <legend>{t.workspace}</legend>
              <label className={profile === "full" ? "selected" : ""}><input type="radio" name="profile" value="full" checked={profile === "full"} onChange={() => setProfile("full")} /><span><strong>{t.heavy}</strong><small>{t.heavyAbout((tiers?.find((tier) => tier.id === "heavy")?.slots ?? 4) / 2)}</small></span></label>
              <label className={profile === "lite" ? "selected" : ""}><input type="radio" name="profile" value="lite" checked={profile === "lite"} onChange={() => setProfile("lite")} /><span><strong>{t.lite}</strong><small>{t.liteAbout((tiers?.find((tier) => tier.id === "lite")?.slots ?? 2) / 2)}</small></span></label>
            </fieldset>
          ) : null}
          {error != null ? <div className="login-error" role="alert">{error}</div> : null}
          <button className="primary login-submit" type="submit" disabled={busy}>{busy ? t.pleaseWait : creating ? t.createAccount : t.signIn}</button>
          {signupUrl == null
            ? <button className="linkish" type="button" disabled={busy} onClick={() => { setCreating(!creating); setError(null); }}>{creating ? t.haveAccount : t.newHere}</button>
            : <a className="linkish" href={signupUrl} target="_blank" rel="noopener noreferrer">{t.newHerePortal}</a>}
          <p className="muted login-footnote">{demo ? t.demoFootnote : t.cloudFootnote}</p>
          <LanguagePicker />
        </form>
      </div>
    </main>
  );
}

export function AuthGate({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const t = useText(LOGIN);
  const [session, setSession] = useState<HostedSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const signedIn = session?.enabled === true && session.authenticated;
  const changed = useCallback((next: HostedSession) => { setSession(next); setError(null); }, []);

  // Load the session once on mount and on a manual retry. A ready or stopped
  // workspace does not change on its own — the box's own stream carries the
  // work — so we do not poll it; that keeps steady-state traffic (and the
  // cloud host's per-request cost) at zero.
  useEffect(() => {
    let cancelled = false;
    hostedApi.session()
      .then((next) => { if (!cancelled) changed(next); })
      .catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : text(LOGIN).cannotReachService); });
    return () => { cancelled = true; };
  }, [changed, retry]);

  // Signed out, or never signed in: no account's chats stay on this device (chat-cache.ts).
  // Run after the app has unmounted, so nothing it had open is written back.
  const signedOut = session?.enabled === true && !session.authenticated;
  useEffect(() => {
    if (!signedOut) return;
    try {
      forgetChats(localStorage);
    } catch {
      // Storage is off: nothing was kept.
    }
  }, [signedOut]);

  // An account without a plan buys one in the portal, usually in another tab or
  // the phone's browser: coming back to this page is when to ask again.
  const blocked = signedIn && session?.blocked != null;
  useEffect(() => {
    if (!blocked) return;
    const check = (): void => {
      if (document.visibilityState === "visible") void hostedApi.session().then(changed).catch(() => { /* asked again on the next return */ });
    };
    document.addEventListener("visibilitychange", check);
    return () => document.removeEventListener("visibilitychange", check);
  }, [blocked, changed]);

  // Only while the workspace is mid-transition do we follow it, until it settles.
  const state = session?.state;
  useEffect(() => {
    if (!signedIn || (state !== "starting" && state !== "stopping" && state !== "resizing")) return;
    let cancelled = false;
    const timer = setInterval(() => {
      void hostedApi.session().then((next) => { if (!cancelled) changed(next); }).catch(() => { /* a dropped poll is retried on the next tick */ });
    }, 2000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [signedIn, state, changed]);

  // Auto-start once when authenticated and the workspace isn't running. This is
  // the on-demand trigger the flow now relies on instead of a manual Start button,
  // and it also recovers a box that did not shut down gracefully (which reconciles
  // to "stopped"). The ref stops it re-firing while a start is in flight or failed.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!signedIn || state === "ready") { autoStarted.current = false; return; }
    if (autoStarted.current || busy || state !== "stopped") return;
    autoStarted.current = true;
    setBusy(true);
    setError(null);
    hostedApi.start()
      .then((next) => changed(next))
      .catch((cause) => setError(cause instanceof Error ? cause.message : text(LOGIN).couldNotStart))
      .finally(() => setBusy(false));
  }, [signedIn, state, busy, changed]);

  const action = useCallback(async (kind: "start" | "restart" | "logout"): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (kind === "logout") {
        await hostedApi.logout();
        setSession((current) => ({ enabled: true, authenticated: false, signupUrl: current?.signupUrl, demo: current?.demo }));
      } else changed(await hostedApi[kind]());
    } finally { setBusy(false); }
  }, [changed]);
  /** The status screen's own buttons say what failed at the top of the page; the menu and Settings report it where they were pressed. */
  const report = (cause: unknown): void => setError(cause instanceof Error ? cause.message : t.operationFailed);

  // What the account menu and Settings get: the session, and the few things they can ask of it.
  const account = useMemo<AccountState | null>(() => (session?.enabled === true && session.authenticated ? {
    session,
    refresh: () => hostedApi.session().then(changed),
    logout: () => action("logout"),
    restart: () => action("restart"),
  } : null), [session, changed, action]);

  if (session == null) return <main className="login-page"><div className="login-card"><div className="brand">ark-bot</div><p role="status">{error ?? t.connecting}</p>{error == null ? null : <button onClick={() => setRetry((n) => n + 1)}>{t.retryConnection}</button>}</div></main>;
  if (!session.enabled) return <>{children}</>;
  if (!session.authenticated) return <Login onAuthenticated={changed} tiers={session.tiers} signupUrl={session.signupUrl} demo={session.demo} />;
  if (session.blocked != null) {
    return (
      <main className="login-page">
        <section className="login-card workspace-status" aria-live="polite">
          <div className="brand">ark-bot</div>
          <h1>{t.needsPlan}</h1>
          <p>{serverText(session.blocked.reason)}</p>
          <p className="muted">{t.plansInPortal}</p>
          {error != null ? <div className="login-error" role="alert">{error}</div> : null}
          {session.blocked.portalUrl == null ? null : (
            <a className="plan-link" href={session.blocked.portalUrl} target="_blank" rel="noopener noreferrer">
              {t.getPlan}
            </a>
          )}
          <button type="button" className="plan-check" disabled={busy} onClick={() => { setError(null); void hostedApi.session().then(changed).catch(report); }}>
            {t.checkAgain}
          </button>
          <p className="muted workspace-account">
            {t.signedInAs(session.account?.username ?? "")}{" "}
            <button type="button" className="linkish" disabled={busy} onClick={() => void action("logout").catch(report)}>{t.logOut}</button>
          </p>
        </section>
      </main>
    );
  }
  const ready = session.state === "ready";
  const pending = session.state === "starting" || session.state === "stopping" || session.state === "resizing";
  const working = pending || busy;

  // The account lives at the foot of the roster (the account menu); the page has no bar of its own for it.
  return (
    <div className="hosted-shell">
      {error != null ? <div className="notice" role="alert">{error}</div> : null}
      <div className="hosted-content" key={session.account?.id}>
        {ready ? <AccountContext.Provider value={account}>{children}</AccountContext.Provider> : (
          <main className="login-page"><section className="login-card workspace-status" aria-live="polite">
            <div className="brand">{session.profile === "full" ? t.fullDesktop : t.liteWorkspace}</div>
            <h1>{session.state === "resizing" ? t.applyingPlan : session.state === "stopping" ? t.restarting : session.state === "error" ? t.needsAttention : t.starting}</h1>
            <p>{session.error ?? (session.state === "error" ? t.lastStartFailed : session.state === "stopping" ? t.backingUp : t.startingAbout)}</p>
            {session.capacity == null ? null : <p className="muted">{t.demoRam(session.capacity.used * 0.5, session.capacity.total * 0.5)}</p>}
            {working ? <progress aria-label={t.inProgress} /> : <button className="primary" disabled={busy} onClick={() => { autoStarted.current = true; void action("start").catch(report); }}>{t.tryAgain}</button>}
            <p className="muted workspace-account">
              {t.signedInAs(session.account?.username ?? "")}{" "}
              <button type="button" className="linkish" disabled={busy} onClick={() => void action("logout").catch(report)}>{t.logOut}</button>
            </p>
          </section></main>
        )}
      </div>
    </div>
  );
}
